(() => {
  if (customElements.get('dio-cinematic-hero')) return;

  const instances = new Set();
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  class DioCinematicHero extends HTMLElement {
    constructor() {
      super();
      this.timers = [];
      this.frames = [];
      this.blocks = [];
      this.started = false;
    }

    connectedCallback() {
      instances.add(this);
      this.start();
    }

    disconnectedCallback() {
      this.revealImmediately();
      instances.delete(this);
    }

    start() {
      if (this.started) return;
      this.started = true;

      try {
        this.blocks = Array.from(this.querySelectorAll('[data-cinematic-reveal]'));
        this.startReveal();
      } catch (error) {
        this.revealImmediately();
      }
    }

    startReveal() {
      if (this.dataset.animationEnabled !== 'true' || reducedMotion.matches || !this.blocks.length) {
        this.revealImmediately();
        return;
      }

      this.classList.add('is-animation-ready');
      this.dataset.animationState = 'ready';
      this.blocks.forEach((block) => {
        if (block.dataset.revealStyle !== 'none') block.inert = true;
      });
      this.timers.push(window.setTimeout(() => this.revealImmediately(), 6500));

      this.frames.push(
        requestAnimationFrame(() => {
          this.frames.push(requestAnimationFrame(() => this.scheduleReveal()));
        })
      );
    }

    scheduleReveal() {
      if (!this.isConnected) return;

      this.blocks.forEach((block) => {
        const delay = block.dataset.revealStyle === 'none' ? 0 : this.readDelay(block);
        this.schedule(() => {
          block.inert = false;
          block.classList.add('is-revealed');
        }, delay);
      });
    }

    readDelay(element) {
      const value = Number.parseInt(element.dataset.revealDelay, 10);
      return Number.isFinite(value) ? Math.min(Math.max(value, 0), 4000) : 0;
    }

    schedule(callback, delay) {
      if (delay === 0) {
        callback();
        return;
      }
      this.timers.push(window.setTimeout(callback, delay));
    }

    revealImmediately() {
      this.clearSchedule();
      this.dataset.animationState = 'visible';
      this.classList.remove('is-animation-ready');
      this.blocks.forEach((block) => {
        block.inert = false;
        block.classList.add('is-revealed');
      });
    }

    clearSchedule() {
      this.timers.forEach((timer) => window.clearTimeout(timer));
      this.frames.forEach((frame) => cancelAnimationFrame(frame));
      this.timers = [];
      this.frames = [];
    }
  }

  const revealFromEvent = (event) => {
    const hero = event.target?.matches?.('dio-cinematic-hero')
      ? event.target
      : event.target?.querySelector?.('dio-cinematic-hero') || event.target?.closest?.('dio-cinematic-hero');
    hero?.revealImmediately();
  };

  document.addEventListener('shopify:section:select', revealFromEvent);
  document.addEventListener('shopify:block:select', revealFromEvent);
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) instances.forEach((hero) => hero.revealImmediately());
  });

  const handleMotionChange = (event) => {
    if (event.matches) instances.forEach((hero) => hero.revealImmediately());
  };
  if (reducedMotion.addEventListener) reducedMotion.addEventListener('change', handleMotionChange);
  else reducedMotion.addListener?.(handleMotionChange);

  customElements.define('dio-cinematic-hero', DioCinematicHero);
})();
